import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import { formatShortDate } from "@/utils/article";

export default function LatestSection() {
  const [activeKey, setActiveKey] = useState<"news" | "lifestyle" | "trend">("news");
  const scrollRef = useRef<HTMLDivElement>(null);

  const list = articles
    .filter((item) => item.category === activeKey)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 6);

  const scrollBy = (direction: number) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction * 340, behavior: "smooth" });
  };

  return (
    <section className="bg-background-100 px-4 py-14 md:px-8 md:py-20 lg:px-12">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            最新内容
          </h2>
          <div className="inline-flex items-center gap-1 rounded-full bg-background-50 p-1">
            {categories.map((category) => (
              <button
                key={category.key}
                type="button"
                onClick={() => setActiveKey(category.key)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors md:px-4 ${
                  activeKey === category.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/${activeKey}`}
            className="mr-2 hidden items-center gap-1 text-sm font-medium text-accent-600 transition-colors hover:text-accent-700 sm:flex"
          >
            查看全部
            <i className="ri-arrow-right-line" />
          </Link>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="向左滚动"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="向右滚动"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-2">
        {list.map((article) => (
          <Link
            key={article.id}
            to={`/detail/${article.id}`}
            className="group w-[280px] shrink-0 snap-start cursor-pointer overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 sm:w-[320px]"
          >
            <div className="aspect-[16/10] w-full overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 台北娜娜`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <div className="mb-2 flex items-center gap-2 text-xs text-foreground-500">
                <span className="font-medium text-accent-600">{article.categoryName}</span>
                <span>·</span>
                <span>{formatShortDate(article.date)}</span>
              </div>
              <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                {article.title}
              </h3>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                {article.summary}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}